import { liteClient as algoliasearch, type LiteClient } from 'algoliasearch/lite';
import { getGrowthbookSync } from 'Shared/features';

export type SearchClientType = LiteClient | null;

const STORE_OFFER_INDEX_FLAG = 'plp-store-offer-index';

export function getSearchClient(): SearchClientType {
   return App.algoliaConfig?.appId
      ? algoliasearch(App.algoliaConfig.appId, App.algoliaConfig.apiKey)
      : null;
}

export const PUBLIC_KEY = (App.algoliaConfig?.apiKey as string) || '';

const indexes: Record<string, string[]> = App.algoliaConfig?.indexNamesByObjectType || {};

const specialIndexToDocTypeMap: Record<string, string> = {
   'searchpage': 'pages',
   'item': 'item',
   'wp_searchable_posts': 'news',
};

const mapIndexToDocType = (index: string): string => specialIndexToDocTypeMap[index] || index;

/**
 * The Algolia indexes the `product` doctype searches.
 *
 * PHP ships the control `product_*` names and the store_offer A/B treatment's
 * names (issue #55186) side by side and picks neither: its GrowthBook SDK gets
 * no uniqueId while server-side A/B testing is off (issue #60223), so it would
 * put every visitor in the control. The browser has the ab_participant cookie,
 * so it picks here, and every search surface that resolves an index through
 * this module or doctypes.js follows the same bucket.
 */
export function productIndexNames(): string[] {
   const control: string[] = indexes.product || [];
   const treatment: string[] = indexes.store_offer || [];

   if (!treatment.length || !getGrowthbookSync().isOn(STORE_OFFER_INDEX_FLAG)) {
      return control;
   }

   return treatment;
}

export function getIndexes(): Record<string, string[]> {
   const wantedDocTypes = App.algoliaConfig?.defaultDoctypes || [];
   const filtered = Object.entries({ ...indexes, product: productIndexNames() })
      .map(([index, value]) => [mapIndexToDocType(index), value] as [string, string[]])
      .filter(([docType]) => wantedDocTypes.includes(docType));

   return toFirstIndexes(Object.fromEntries(filtered));
}

export function isProUser(): boolean {
   const storeCode = App?.userPreferredStoreCode || '';
   return App?.priceTiers[storeCode]?.test(/pro/i) || false;
}

function toFirstIndexes(indices: Record<string, string[]>): Record<string, string[]> {
   return Object.fromEntries(
      Object.entries(indices).map(([doctype, indexNames]) => [doctype, [indexNames[0]]])
   );
}
